import { useTranslation } from 'react-i18next'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import Alert from '@/shared/components/Alert/Alert'
import TimePicker from '@/shared/components/TimePicker/TimePicker'
import { formatPrice } from '@/shared/utils/price'
import {
  NIGHT_EARLIEST_START_MINUTES,
  NIGHT_LATEST_START_MINUTES,
  PayrollRow,
  nightEndMinutes,
  sumPay
} from '../../utils/payroll'
import {
  dayjsToMinutes,
  formatDuration,
  formatMinutes,
  minutesToDayjs
} from '../../utils/schedule'
import { DEFAULT_SHIFT_TYPE, SHIFT_TYPES } from '../../types/shiftType.model'
import styles from './PayrollSummary.module.scss'

interface PayrollSummaryProps {
  /** Computed upstream, because the caveats derived from it live above the
      view switcher, see usePayrollRows. */
  rows: PayrollRow[]
  /** Rows whose pay could not be computed, and rows understated by premiums */
  missingRate: PayrollRow[]
  withPremiums: PayrollRow[]
  /** At least one week of the reported month could not be read from the API */
  isIncomplete?: boolean
  /** Pay rates could not be read at all, so no row has one */
  ratesForbidden?: boolean
  employeeColor: (userId: string) => string
  isLoading?: boolean
  /** First minute of the venue's designated night window. */
  nightStartMinutes: number
  onNightStartChange: (minutes: number) => void
}

const formatHours = (hours: number): string =>
  `${hours.toFixed(hours % 1 === 0 ? 0 : 1)}`

function PayrollSummary(props: PayrollSummaryProps) {
  const {
    rows,
    missingRate,
    withPremiums,
    isIncomplete,
    ratesForbidden,
    employeeColor,
    isLoading,
    nightStartMinutes,
    onNightStartChange
  } = props
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className={styles.summary}>
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton
            key={`payroll-skeleton-${index}`}
            variant="rounded"
            width="100%"
            height="3rem"
          />
        ))}
      </div>
    )
  }

  return (
    <div className={styles.summary}>
      {/*
        Only the start is picked. The length is not ours to choose — the law
        fixes it at eight hours — so the end is derived and shown read-only,
        which also makes it obvious that moving the start moves both.
      */}
      <div className={styles.nightWindow}>
        <div className={styles.nightWindowField}>
          <TimePicker
            label={t('PAYROLL.NIGHT_WINDOW_LABEL')}
            // The roster stores minutes from midnight; the pair of converters
            // it already uses for shift times does the translation here too.
            value={minutesToDayjs(nightStartMinutes)}
            onChange={value => {
              const minutes = dayjsToMinutes(value)
              if (minutes !== null) onNightStartChange(minutes)
            }}
            minTime={minutesToDayjs(NIGHT_EARLIEST_START_MINUTES) ?? undefined}
            maxTime={minutesToDayjs(NIGHT_LATEST_START_MINUTES) ?? undefined}
            minutesStep={30}
            pinnedLabel
          />
        </div>
        <p className={`body-medium ${styles.nightWindowHint}`}>
          {t('PAYROLL.NIGHT_WINDOW_HINT', {
            from: formatMinutes(nightStartMinutes),
            to: formatMinutes(nightEndMinutes(nightStartMinutes))
          })}
        </p>
      </div>
      <div className={styles.table}>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('SCHEDULE.EMPLOYEE')}
        </div>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('PAYROLL.HOURS_TO_DATE')}
        </div>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('PAYROLL.HOURS_SCHEDULED')}
        </div>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('PAYROLL.OVERTIME')}
        </div>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('PAYROLL.NIGHT')}
        </div>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('PAYROLL.SUNDAY')}
        </div>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('PAYROLL.PAY_TO_DATE')}
        </div>
        <div className={`${styles.headerCell} body-bold-medium`}>
          {t('PAYROLL.PAY_ESTIMATED')}
        </div>

        {rows.map(row => {
          /*
            Only the types this person actually has, and never working time:
            that already has four columns of its own above. Seven more columns
            would not fit the card, and a row that lists nothing is not drawn at
            all, so a roster of ordinary shifts looks exactly as it did.
          */
          const breakdown = SHIFT_TYPES.filter(
            definition =>
              definition.type !== DEFAULT_SHIFT_TYPE &&
              (row.byType[definition.type].hours > 0 ||
                row.byType[definition.type].days > 0)
          )

          return [
            <div key={`${row.userId}-name`} className={styles.nameCell}>
              <span
                className={styles.colorDot}
                style={{ backgroundColor: employeeColor(row.userId) }}
              />
              <span className="body-bold-medium">{row.name}</span>
            </div>,
            <div
              key={`${row.userId}-hours`}
              className={`body-medium ${styles.cell}`}
            >
              {formatHours(row.hoursToDate)}
            </div>,
            <div
              key={`${row.userId}-scheduled`}
              className={`body-medium ${styles.cell} ${
                row.exceedsContractedHours ? styles.warning : ''
              }`}
              title={
                row.exceedsContractedHours
                  ? t('PAYROLL.OVER_CONTRACTED_HOURS')
                  : undefined
              }
            >
              {formatHours(row.hoursScheduled)}
            </div>,
            <div
              key={`${row.userId}-overtime`}
              className={`body-medium ${styles.cell} ${
                row.overtimeHours ? styles.warning : ''
              }`}
            >
              {formatHours(row.overtimeHours)}
            </div>,
            <div
              key={`${row.userId}-night`}
              className={`body-medium ${styles.cell}`}
            >
              {formatHours(row.nightHours)}
            </div>,
            <div
              key={`${row.userId}-sunday`}
              className={`body-medium ${styles.cell}`}
            >
              {formatHours(row.sundayHours)}
            </div>,
            <div
              key={`${row.userId}-pay`}
              className={`body-medium ${styles.cell}`}
            >
              {row.payToDateMinorUnits === undefined
                ? '—'
                : formatPrice(row.payToDateMinorUnits)}
            </div>,
            <div
              key={`${row.userId}-estimate`}
              className={`body-bold-medium ${styles.cell}`}
            >
              {row.payEstimatedMinorUnits === undefined
                ? t('PAYROLL.NO_RATE')
                : formatPrice(row.payEstimatedMinorUnits)}
            </div>,
            breakdown.length ? (
              <div
                key={`${row.userId}-breakdown`}
                className={`body-small ${styles.breakdownCell}`}
              >
                {breakdown.map(definition => (
                  <span key={definition.type} className={styles.breakdownItem}>
                    <span className={styles.breakdownLabel}>
                      {t(definition.labelKey)}
                    </span>
                    {definition.allDay
                      ? t('SCHEDULE.DAYS_SHORT', {
                          days: row.byType[definition.type].days
                        })
                      : formatDuration(row.byType[definition.type].hours, t)}
                  </span>
                ))}
              </div>
            ) : null
          ]
        })}

        <div
          className={`${styles.totalCell} ${styles.totalLabel} body-bold-medium`}
        >
          {t('PAYROLL.TOTAL')}
        </div>
        <div className={styles.totalCell} />
        <div className={styles.totalCell} />
        <div className={styles.totalCell} />
        <div className={styles.totalCell} />
        <div className={styles.totalCell} />
        <div className={`${styles.totalCell} body-bold-medium`}>
          {formatPrice(sumPay(rows, 'payToDateMinorUnits'))}
        </div>
        <div className={`${styles.totalCell} body-bold-medium`}>
          {formatPrice(sumPay(rows, 'payEstimatedMinorUnits'))}
        </div>
      </div>

      {/*
        Under the table, next to the figures they qualify. Kept as alerts
        rather than grey footnotes, plain small text here went unread.
      */}
      <div className={styles.notes}>
        {/* First, because it qualifies every figure above rather than a row. */}
        {isIncomplete && (
          <Alert
            severity="error"
            message={t('PAYROLL.NOTE_INCOMPLETE')}
            onClose={() => undefined}
          />
        )}
        {ratesForbidden && (
          <Alert
            severity="error"
            message={t('PAYROLL.NOTE_RATES_FORBIDDEN')}
            onClose={() => undefined}
          />
        )}
        {/*
          Suppressed when the rates could not be read: naming every employee as
          one nobody set a rate for would be a claim about the data that the
          note above has just contradicted.
        */}
        {!ratesForbidden && !!missingRate.length && (
          <Alert
            severity="warning"
            message={t('PAYROLL.NOTE_MISSING_RATE', {
              count: missingRate.length,
              names: missingRate.map(row => row.name).join(', ')
            })}
            onClose={() => undefined}
          />
        )}
        {!!withPremiums.length && (
          <Alert
            severity="info"
            message={t('PAYROLL.NOTE_PREMIUMS')}
            onClose={() => undefined}
          />
        )}
      </div>
    </div>
  )
}

export default PayrollSummary
